import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface TestResponse {
  response: string;
  model: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  cost: number;
  responseTime: number;
  timestamp: string;
}

export const LLMAPITestPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<TestResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getCSRFToken = (): string => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('csrf_token='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };

  const testLLMAPI = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to test');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('=== LLMAPI Test Debug Info ===');
      
      // Debug: Check cookies and CSRF token
      const csrfToken = getCSRFToken();
      console.log('CSRF Token:', csrfToken || 'NOT FOUND');
      console.log('All Cookies:', document.cookie);
      
      // Debug: Request payload
      const requestPayload = {
        message: prompt,
        context: context || undefined,
        sessionId: crypto.randomUUID()
      };
      console.log('Request Payload:', requestPayload);
      
      // Debug: Request headers
      const requestHeaders = {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      };
      console.log('Request Headers:', requestHeaders);
      
      const startTime = Date.now();
      console.log('Making request to:', '/api/ai/chat');
      console.log('Request method:', 'POST');
      
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestPayload),
      });

      const responseTime = Date.now() - startTime;
      console.log('Response Status:', response.status);
      console.log('Response Status Text:', response.statusText);
      console.log('Response Headers:', Object.fromEntries(response.headers.entries()));
      console.log('Response Time:', responseTime + 'ms');

      if (!response.ok) {
        let errorData: any = {};
        let errorText = '';
        
        try {
          const responseText = await response.text();
          console.log('Raw Response Text:', responseText);
          
          if (responseText) {
            try {
              errorData = JSON.parse(responseText);
              console.log('Parsed Error Data:', errorData);
            } catch (parseError) {
              console.log('Failed to parse error response as JSON:', parseError);
              errorText = responseText;
            }
          }
        } catch (textError) {
          console.log('Failed to read response text:', textError);
        }

        const errorMessage = errorData.error || errorText || `HTTP ${response.status}: ${response.statusText}`;
        console.log('Final Error Message:', errorMessage);
        throw new Error(errorMessage);
      }

      console.log('Response OK, parsing JSON...');
      const data = await response.json() as any;
      console.log('Response Data:', data);
      
      const testResponse: TestResponse = {
        response: data.response,
        model: data.model,
        usage: data.usage,
        cost: data.cost,
        responseTime: data.responseTime || responseTime,
        timestamp: new Date().toISOString()
      };

      setResponses(prev => [testResponse, ...prev]);
      console.log('LLMAPI Test Success:', testResponse);
      console.log('=== End Debug Info ===');
      
    } catch (err) {
      console.log('=== LLMAPI Test Error ===');
      console.log('Error Type:', typeof err);
      console.log('Error Instance:', err);
      console.log('Error Name:', err instanceof Error ? err.name : 'Unknown');
      console.log('Error Message:', err instanceof Error ? err.message : 'Unknown error');
      console.log('Error Stack:', err instanceof Error ? err.stack : 'No stack trace');
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('LLMAPI Test Error:', err);
      console.log('=== End Error Debug ===');
    } finally {
      setIsLoading(false);
    }
  };

  const clearResponses = () => {
    setResponses([]);
    setError(null);
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(6)}`;
  };

  const formatTokens = (tokens: number) => {
    return tokens.toLocaleString();
  };

  const getModelTier = (model: string) => {
    if (model.includes('deepseek-r1')) return { tier: 'Flagship', color: 'text-red-600 bg-red-50' };
    if (model.includes('qwen2.5-72b') || model.includes('llama3.3-70b')) return { tier: 'Flagship', color: 'text-red-600 bg-red-50' };
    if (model.includes('gemma3-27b') || model.includes('qwen2.5-coder-32b')) return { tier: 'Premium', color: 'text-orange-600 bg-orange-50' };
    return { tier: 'Standard', color: 'text-green-600 bg-green-50' };
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header with Navigation */}
      <header className="bg-white border-b border-gray-200 px-4 py-2 flex justify-between items-center">
        <a 
          href="/"
          className="text-xl font-semibold text-accent-purple hover:text-accent-purple-dark transition-colors cursor-pointer no-underline"
        >
          AddWise AI
        </a>
        <div className="flex items-center space-x-4">
          {/* Navigation Links */}
          <button
            onClick={() => navigate('/')}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            Neu
          </button>

          <button
            onClick={() => navigate('/tasks')}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            Tasks
          </button>
          <button
            onClick={() => navigate('/clients')}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            Clients
          </button>
          <button
            onClick={() => navigate('/test-llmapi')}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-blue-100 text-blue-700"
          >
            API Test
          </button>
          <div className="w-px h-6 bg-gray-300"></div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-accent-purple transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">🧪 LLMAPI Integration Test</h1>
            <p className="text-gray-600">
              Test the LLMAPI integration with different complexity levels and models. 
              This helps verify the connection and quality before processing actual transcripts.
            </p>
          </div>

          {/* Test Input Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Test Configuration</h2>
            
            <div className="space-y-4">
              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Prompt
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter your test prompt here... (e.g., 'Explain quantum computing in simple terms' or 'Analyze the patterns in customer feedback data')"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Context Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Context (Optional)
                </label>
                <textarea
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Add context to simulate transcript data... (e.g., 'Meeting notes: Discussed Q4 strategy, budget concerns, team expansion')"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>

              {/* Test Button */}
              <div className="flex space-x-4">
                <button
                  onClick={testLLMAPI}
                  disabled={isLoading || !prompt.trim()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Testing...
                    </>
                  ) : (
                    '🚀 Test LLMAPI'
                  )}
                </button>
                
                {responses.length > 0 && (
                  <button
                    onClick={clearResponses}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                  >
                    Clear Results
                  </button>
                )}
              </div>
              
              {/* Information about automatic complexity detection */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="text-blue-600 mr-2">ℹ️</div>
                  <div>
                    <h4 className="text-blue-800 font-medium mb-1">Automatic Model Selection</h4>
                    <p className="text-blue-700 text-sm">
                      The system automatically selects the best model based on your prompt complexity and context length:
                    </p>
                    <ul className="text-blue-700 text-sm mt-2 space-y-1">
                      <li>• <strong>Simple tasks</strong> → Gemma3-12B ($0.0006/1k tokens) for quick responses</li>
                      <li>• <strong>Medium tasks</strong> → Gemma3-27B ($0.0016/1k tokens) for detailed analysis</li>
                      <li>• <strong>Complex tasks</strong> → Mixtral-8x7B-Instruct ($0.0028/1k tokens) for advanced reasoning</li>
                      <li>• <strong>Research tasks</strong> → Llama3.3-70B ($0.0028/1k tokens) for comprehensive insights</li>
                    </ul>
                    <p className="text-blue-600 text-xs mt-2">
                      <strong>Complexity Detection:</strong> Based on message length (&gt;500 chars for medium, &gt;2000 for complex) 
                      and keywords like "analyze", "compare", "insights", "patterns", etc.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="text-red-600 mr-2">❌</div>
                <div>
                  <h3 className="text-red-800 font-medium">Connection Error</h3>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          {responses.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Test Results ({responses.length})</h2>
              
              {responses.map((result, index) => {
                const modelInfo = getModelTier(result.model);
                
                return (
                  <div key={index} className="bg-white rounded-lg shadow-md p-6">
                    {/* Response Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-green-600 text-xl">✅</span>
                        <span className="text-sm text-gray-500">
                          {new Date(result.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${modelInfo.color}`}>
                          {modelInfo.tier}
                        </span>
                        <span className="text-sm font-mono text-gray-600">
                          {result.model}
                        </span>
                      </div>
                    </div>

                    {/* Response Content */}
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">AI Response:</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-800 whitespace-pre-wrap">{result.response}</p>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-blue-600 font-medium">Response Time</div>
                        <div className="text-blue-800 font-mono">{result.responseTime}ms</div>
                      </div>
                      
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="text-green-600 font-medium">Cost</div>
                        <div className="text-green-800 font-mono">{formatCost(result.cost)}</div>
                      </div>
                      
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="text-purple-600 font-medium">Total Tokens</div>
                        <div className="text-purple-800 font-mono">{formatTokens(result.usage.total_tokens)}</div>
                      </div>
                      
                      <div className="bg-orange-50 p-3 rounded-lg">
                        <div className="text-orange-600 font-medium">Token Ratio</div>
                        <div className="text-orange-800 font-mono">
                          {result.usage.prompt_tokens}→{result.usage.completion_tokens}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Summary Stats */}
              {responses.length > 1 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold mb-3">Test Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Total Tests</div>
                      <div className="font-mono font-medium">{responses.length}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Avg Response Time</div>
                      <div className="font-mono font-medium">
                        {Math.round(responses.reduce((sum, r) => sum + r.responseTime, 0) / responses.length)}ms
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Total Cost</div>
                      <div className="font-mono font-medium">
                        {formatCost(responses.reduce((sum, r) => sum + r.cost, 0))}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Models Used</div>
                      <div className="font-mono font-medium">
                        {new Set(responses.map(r => r.model)).size}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">🔍 Testing Guide</h3>
            <div className="text-blue-800 text-sm space-y-2">
              <p><strong>Simple Tasks (Auto-detected):</strong> "What is machine learning?" or "Define artificial intelligence"</p>
              <p><strong>Medium Tasks (Auto-detected):</strong> "Analyze the pros and cons of remote work" or "Compare different project management methodologies"</p>
              <p><strong>Complex Tasks (Auto-detected):</strong> "Explain the implications of quantum computing on cryptography" or "Analyze market trends and provide strategic recommendations"</p>
              <p><strong>Context Testing:</strong> Add meeting notes or transcript data in the context field to test contextual analysis capabilities</p>
              <p><strong>Financial Example:</strong> Try the pre-filled financial advice prompt to test domain-specific analysis</p>
            </div>

            {/* Example Test Buttons */}
            <div className="mt-4">
              <h4 className="font-medium text-blue-900 mb-3">🧪 Quick Test Examples:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  onClick={() => {
                    setPrompt("What is machine learning?");
                    setContext("");
                  }}
                  className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  🟢 Simple: Quick Question
                </button>
                
                <button
                  onClick={() => {
                    setPrompt("Analyze the pros and cons of implementing AI in customer service. Consider both technical feasibility and business impact.");
                    setContext("Company context: Mid-size SaaS company with 500 employees, currently using traditional support tickets");
                  }}
                  className="bg-orange-100 hover:bg-orange-200 text-orange-800 px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  🟡 Medium: Business Analysis
                </button>
                
                <button
                  onClick={() => {
                    setPrompt("Perform a comprehensive cross-transcript analysis to identify recurring patterns, strategic insights, and actionable recommendations across multiple client interactions. Analyze the evolution of topics over time, relationship patterns between different discussions, hidden connections and correlations, and provide research-grade analysis with specific examples and evidence.");
                    setContext("Dataset context: 50+ financial advisory transcripts spanning 12 months, covering insurance policies, investment strategies, retirement planning, estate planning, and risk management discussions across diverse client demographics and financial situations.");
                  }}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  🔴 Complex: Research Analysis
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 